import React, { useEffect, useMemo, useState } from 'react';
import { Box, Card, CardContent, Grid, TextField, MenuItem, Table, TableHead, TableRow, TableCell, TableBody, Button, LinearProgress, Stack, Typography, TableContainer, Paper } from '@mui/material';
import { printReport, textCol, numCol } from '../../utils/pdfPrint';
import { useTranslation } from 'react-i18next';
import apiService from '../../services/apiServices';
import PageHeader from '../common/PageHeader';
import SendReportEmailDialog from '../common/SendReportEmailDialog';
import { Link } from 'react-router-dom';
import { ReportPage, ExportMenu } from './ReportKit';

// Note: If attendance data (hyrje/dalje) is not available, we approximate with extra-hours and leave days
export default function PresenceReport() {
  const { t } = useTranslation();
  const [lists, setLists] = useState([]);
  const [listId, setListId] = useState('');
  const [salaries, setSalaries] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [currentList, setCurrentList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailOpen, setEmailOpen] = useState(false);

  useEffect(() => {
    Promise.all([
      apiService().get('salariesmonthlylists'),
      apiService().get('holidays'),
    ]).then(([lr, hr]) => {
      if (lr?.success) setLists(lr.data || []);
      if (hr?.success) setHolidays(hr.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!listId) return setSalaries([]);
    apiService().query('getSalariesByListId', `id=${listId}`).then((r)=> { if(r?.success) setSalaries(r.data||[]); });
    const l = (lists||[]).find((x)=> String(x.id)===String(listId)); setCurrentList(l||null);
  }, [listId, lists]);

  const fullName = (r) => r?.FullName || r?.fullName || `${r?.FirstName || r?.firstname || ''} ${r?.LastName || r?.lastname || ''}`.trim();

  const workingDays = useMemo(()=>{
    if(!currentList) return 0; const y=currentList.year; const m=currentList.month-1; const start=new Date(y,m,1); const end=new Date(y,m+1,0);
    let wd=0; for (let d=new Date(start); d<=end; d.setDate(d.getDate()+1)) { const day=d.getDay(); if (day!==0 && day!==6) wd++; }
    return wd;
  },[currentList]);

  const rows = useMemo(()=>{
    // Pre-build a worker name lookup for the current payroll list
    const workerNames = new Map();
    (salaries||[]).forEach((s) => {
      const name = fullName(s).trim().toLowerCase();
      workerNames.set(String(s.WorkerId || s.Employee || s.Id || ''), name);
    });

    const matchesWorker = (h, s) => {
      const wId = String(s.WorkerId || s.Employee || s.Id || '');
      // Match by numeric WorkerId
      if (h.WorkerId != null && String(h.WorkerId) === wId) return true;
      // Match by Employee field: if numeric, compare as ID
      if (h.Employee != null) {
        const emp = String(h.Employee);
        if (/^\d+$/.test(emp) && emp === wId) return true;
        // If string name, compare to worker full name
        const sName = workerNames.get(wId) || '';
        if (sName && emp.trim().toLowerCase() === sName) return true;
      }
      return false;
    };

    return (salaries||[]).map((s)=>{
      const workerHolidays = (holidays||[]).filter((h) => matchesWorker(h, s));
      const overtime = (Number(s.ExtraHours)||0) + (Number(s.ExtraHoursWeekend)||0) + (Number(s.ExtraHoursHoliday)||0);
      const usedLeave = workerHolidays.length;
      const leaveDays = workerHolidays.reduce((a, b) => a + (Number(b.holidayDays || b.HolidayDays || b.Days) || 0), 0);
      const worked = workingDays ? Math.max(0, workingDays - leaveDays) : '';
      const absent = 0;
      return { name: fullName(s), workDays: worked, absent, usedLeave, leaveDays, overtime };
    });
  },[salaries, holidays, workingDays]);

  function exportCsv(){
    const headers=[t('report.name'), t('report.workingDays'), t('report.absenceDays'), t('report.leavesCount'), t('report.leaveDays'), t('report.overtime')];
    const lines=[headers.join(',')];
    rows.forEach((r)=>{
      const line=[r.name, r.workDays, r.absent, r.usedLeave, r.leaveDays, r.overtime].map((x)=>`"${String(x).replace(/"/g,'""')}"`).join(',');
      lines.push(line);
    });
    const csv=lines.join('\r\n');
    const blob=new Blob([csv],{type:'text/csv;charset=utf-8;'});
    const url=URL.createObjectURL(blob); const a=document.createElement('a'); a.href=url; a.download='presence_report.csv'; a.click(); URL.revokeObjectURL(url);
  }

  function exportXls(){
    const headers=[t('report.worker'), t('report.workingDays'), t('report.absenceDays'), t('report.leavesCount'), t('report.leaveDays'), t('report.overtime')];
    const rowsHtml = rows.map(r=> `<tr><td>${r.name}</td><td>${r.workDays||''}</td><td>${r.absent||''}</td><td>${r.usedLeave||''}</td><td>${r.leaveDays||''}</td><td>${r.overtime||''}</td></tr>`).join('');
    const html = `<!DOCTYPE html><html><head><meta charset='UTF-8'></head><body><table border='1'><thead><tr>${headers.map(h=>`<th>${h}</th>`).join('')}</tr></thead><tbody>${rowsHtml}</tbody></table></body></html>`;
    const blob=new Blob([html],{type:'application/vnd.ms-excel'}); const url=URL.createObjectURL(blob); const a=document.createElement('a'); a.href=url; a.download='presence_report.xls'; a.click(); URL.revokeObjectURL(url);
  }

  function exportPdf() {
    const selectedList = lists.find((l) => String(l.id) === String(listId));
    const period = selectedList ? `${selectedList.month}/${selectedList.year}` : '';
    printReport({
      title: t('reports.inspectoratPresence'),
      subtitle: period ? `${t('report.period')}: ${period} | ${t('report.workingDays')}: ${workingDays}` : '',
      orientation: 'landscape',
      columns: [
        textCol(t('report.worker'), 'name', '30%'),
        numCol(t('report.workingDays'), 'workDays', '13%'),
        numCol(t('report.absenceDays'), 'absent', '11%'),
        numCol(t('report.leavesCount'), 'usedLeave', '13%'),
        numCol(t('report.leaveDays'), 'leaveDays', '13%'),
        numCol(t('report.overtime'), 'overtime', '12%'),
      ],
      rows,
      totals: { workDays: totalWorkDays, leaveDays: totalLeaveDays, overtime: totalOvertime },
      period,
      badge: { label: `${rows.length} ${t('report.workers')}` },
    });
  }

  const totalWorkDays = useMemo(() => rows.reduce((a, r) => a + (Number(r.workDays) || 0), 0), [rows]);
  const totalLeaveDays = useMemo(() => rows.reduce((a, r) => a + r.leaveDays, 0), [rows]);
  const totalOvertime = useMemo(() => rows.reduce((a, r) => a + r.overtime, 0), [rows]);

  return (
    <ReportPage>
      <PageHeader
        title={t('reports.inspectoratPresence')}
        actions={<Button component={Link} to="/home/reports" variant="outlined">{t('report.backToReports')}</Button>}
      />
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={6} md={4}>
              <TextField select fullWidth label={t('report.payslip')} size="small" value={listId} onChange={(e) => setListId(e.target.value)}>
                <MenuItem value="">{t('report.selectList')}</MenuItem>
                {lists.map((l) => (<MenuItem key={l.id} value={l.id}>{`${l.month}/${l.year}`}</MenuItem>))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm="auto"><ExportMenu onCsv={exportCsv} onExcel={exportXls} onPdf={exportPdf} onEmail={() => setEmailOpen(true)} disabled={!listId} /></Grid>
          </Grid>
        </CardContent>
      </Card>

      {listId && rows.length > 0 && (
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          {[
            { label: t('report.monthlyWorkDays'), value: workingDays },
            { label: t('report.workers'), value: rows.length },
            { label: t('report.totalLeaveDays'), value: totalLeaveDays },
            { label: t('report.totalOvertimeArg'), value: totalOvertime },
          ].map((item) => (
            <Card key={item.label} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', flex: 1 }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{item.value}</Typography>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {!listId && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">{t('report.selectMonthlyList')}</Typography>
            </Box>
          )}
          {listId && (
            <TableContainer component={Paper} elevation={0}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>{t('report.worker')}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{t('report.workingDays')}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{t('report.absenceDays')}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{t('report.leavesCount')}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{t('report.leaveDays')}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{t('report.overtime')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        {t('report.noData')}
                      </TableCell>
                    </TableRow>
                  )}
                  {rows.map((r, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{r.name}</TableCell>
                      <TableCell align="right">{r.workDays}</TableCell>
                      <TableCell align="right">{r.absent || '—'}</TableCell>
                      <TableCell align="right">{r.usedLeave || '—'}</TableCell>
                      <TableCell align="right">{r.leaveDays || '—'}</TableCell>
                      <TableCell align="right">{r.overtime || '—'}</TableCell>
                    </TableRow>
                  ))}
                  {rows.length > 0 && (
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell sx={{ fontWeight: 700 }}>{t('report.totalRow')}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{totalWorkDays}</TableCell>
                      <TableCell align="right">—</TableCell>
                      <TableCell align="right">—</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{totalLeaveDays}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{totalOvertime}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <SendReportEmailDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        reportTitle={t('reports.inspectoratPresence')}
        subtitle={currentList ? `${currentList.month}/${currentList.year} — ${t('report.workingDays')}: ${workingDays}` : ''}
        columns={[
          { label: t('report.worker'), key: 'name', align: 'left' },
          { label: t('report.workingDays'), key: 'workDays', align: 'right' },
          { label: t('report.leavesCount'), key: 'usedLeave', align: 'right' },
          { label: t('report.leaveDays'), key: 'leaveDays', align: 'right' },
          { label: t('report.overtime'), key: 'overtime', align: 'right' },
        ]}
        rows={rows}
        totals={{ workDays: totalWorkDays, leaveDays: totalLeaveDays, overtime: totalOvertime }}
        workers={salaries}
      />
    </ReportPage>
  );
}
